import React, { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus, DotsSixVertical, Trash } from "@phosphor-icons/react";
import api from "@/lib/api";
import { eur } from "@/lib/format";
import { PageHeader } from "@/components/common";
import { ScoreRing, PriorityChip } from "@/components/ScoreCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useTranslation } from 'react-i18next'

const STAGE_COLOR = (i, total) => {
  if (i === total - 1) return "#ef4444";
  if (i === total - 2) return "#2f9e6f";
  if (i >= total - 4) return "#C5A059";
  return "#0B2545";
};

export default function Pipeline() {
  const { t } = useTranslation()
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: "", stage: "New lead", value: "", commission: "", probability: 50, next_action: "", contact_name: "", property_title: "" });
  const [dragId, setDragId] = useState(null);

  const { data: meta } = useQuery({ queryKey: ["meta"], queryFn: async () => (await api.get("/meta")).data });
  const { data: opps = [] } = useQuery({ queryKey: ["opportunities"], queryFn: async () => (await api.get("/opportunities")).data });
  const { data: scoredOpps = [] } = useQuery({ queryKey: ["scoring-opportunities"], queryFn: async () => (await api.get("/scoring/opportunities")).data });
  const oppScore = React.useMemo(() => {
    const m = {};
    scoredOpps.forEach((s) => { m[s.opportunity.id] = s.score; });
    return m;
  }, [scoredOpps]);
  const { data: properties = [] } = useQuery({ queryKey: ["properties", {}], queryFn: async () => (await api.get("/properties")).data });
  const { data: contacts = [] } = useQuery({ queryKey: ["contacts", {}], queryFn: async () => (await api.get("/contacts")).data });

  useEffect(() => { if (params.get("new") === "1") { setShowForm(true); setParams({}); } }, [params, setParams]);

  const create = useMutation({
    mutationFn: async (payload) => (await api.post("/opportunities", payload)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["opportunities"] }); setShowForm(false); toast.success("Opportunity created"); },
  });
  const move = useMutation({
    mutationFn: async ({ id, stage }) => (await api.put(`/opportunities/${id}`, { stage })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["opportunities"] }),
  });
  const remove = useMutation({
    mutationFn: async (id) => (await api.delete(`/opportunities/${id}`)).data,
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ["opportunities"] });
      if (data?.pending_approval) toast.info(t("perm.deleteRequested", "Deletion request sent for Admin approval."));
      else toast.success(t("removed", "Removed"));
    },
    onError: () => toast.error(t("actionFailed", "Action failed")),
  });

  const stages = meta?.pipeline_stages || [];

  const pipelineFilter = params.get("filter");
  const matchPipelineFilter = (o) => {
    if (pipelineFilter === "new") return o.stage === "New lead";
    if (pipelineFilter === "offers") return /offer/i.test(o.stage || "");
    return true;
  };

  const submit = () => {
    const payload = { ...form };
    ["value", "commission", "probability"].forEach((k) => { if (payload[k] === "") delete payload[k]; else payload[k] = Number(payload[k]); });
    create.mutate(payload);
  };

  return (
    <div>
      <PageHeader
        title={t('salesPipeline', 'Sales Pipeline')}
        subtitle={t('pipelineSubtitle', 'Track opportunities across 15 stages')}
        actions={
          <Button data-testid="add-opportunity-btn" onClick={() => setShowForm(true)} className="rounded-sm bg-[#0B2545] hover:bg-[#1a365d] text-white">
            <Plus size={16} className="mr-1.5" /> {t('addOpportunity', 'Add Opportunity')}
          </Button>
        }
      />

      {pipelineFilter && (
        <div className="mb-4 flex items-center gap-2" data-testid="pipeline-filter-chip">
          <span className="text-xs px-2 py-1 rounded-sm bg-[#C5A059]/15 text-[#8a6d2f]">
            {pipelineFilter === "new" ? t('newOpportunities', 'New Opportunities') : t('offersAwaiting', 'Offers Awaiting')}
          </span>
          <button onClick={() => setParams({})} data-testid="pipeline-filter-clear" className="text-xs text-[#C5A059] hover:underline">{t('clearFilter', 'Clear filter')}</button>
        </div>
      )}

      <div className="flex gap-4 overflow-x-auto snap-x pb-4" data-testid="kanban-board">
        {stages.map((stage, i) => {
          const items = opps.filter((o) => o.stage === stage && matchPipelineFilter(o));
          const color = STAGE_COLOR(i, stages.length);
          return (
            <div
              key={stage}
              data-testid={`kanban-stage-${stage.toLowerCase().replace(/\s+/g, "-")}`}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => { if (dragId) { move.mutate({ id: dragId, stage }); setDragId(null); } }}
              className="min-w-[290px] w-[290px] flex-shrink-0 snap-start bg-slate-50 border border-slate-200 rounded-sm"
            >
              <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-slate-50">
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full" style={{ backgroundColor: color }} />
                  <span className="text-sm font-semibold text-slate-700">{t(`pipelineStages.${stage}`, stage)}</span>
                </div>
                <span className="text-xs bg-white border border-slate-200 rounded-sm px-1.5 py-0.5 text-slate-500">{items.length}</span>
              </div>
              <div className="p-2 space-y-2 min-h-[120px]">
                {items.map((o) => (
                  <div
                    key={o.id}
                    draggable
                    onDragStart={() => setDragId(o.id)}
                    data-testid={`opp-card-${o.id}`}
                    className="bg-white border border-slate-200 rounded-sm p-3 cursor-grab active:cursor-grabbing hover:border-[#C5A059] transition-colors group"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="text-sm font-medium text-slate-900 leading-snug">{o.title}</div>
                      <button onClick={() => remove.mutate(o.id)} className="opacity-0 group-hover:opacity-100 text-slate-300 hover:text-red-500"><Trash size={14} /></button>
                    </div>
                    {o.property_title && <div className="text-xs text-slate-400 mt-1 truncate">{o.property_title}</div>}
                    {o.contact_name && <div className="text-xs text-slate-500 mt-0.5">{o.contact_name}</div>}
                    {oppScore[o.id] && (
                      <div className="flex items-center gap-2 mt-2">
                        <ScoreRing value={oppScore[o.id].overall} priority={oppScore[o.id].priority} size={30} />
                        <PriorityChip priority={oppScore[o.id].priority} />
                      </div>
                    )}
                    <div className="flex items-center justify-between mt-2 pt-2 border-t border-slate-50">
                      <span className="text-xs font-semibold text-[#0B2545]">{o.value ? eur(o.value) : "—"}</span>
                      {o.probability != null && <span className="text-[10px] text-slate-400">{t('probability2', '{{probability}}%', { probability: o.probability })}</span>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-lg bg-white rounded-sm">
          <DialogHeader><DialogTitle className="font-serif text-2xl">{t('addOpportunity', 'Add Opportunity')}</DialogTitle><DialogDescription className="text-sm text-slate-500">{t('addOpportunityDesc', 'Create a new opportunity and place it in the right pipeline stage.')}</DialogDescription></DialogHeader>
          <div className="space-y-4">
            <div><Label>{t('title', 'Title *')}</Label><Input data-testid="opp-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1 rounded-sm" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>{t('stage', 'Stage')}</Label>
                <Select value={form.stage} onValueChange={(v) => setForm({ ...form, stage: v })}>
                  <SelectTrigger className="mt-1 rounded-sm bg-white"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white max-h-64">{stages.map((s) => <SelectItem key={s} value={s}>{t(`pipelineStages.${s}`, s)}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>{t('probability', 'Probability %')}</Label>
                <Input type="number" value={form.probability} onChange={(e) => setForm({ ...form, probability: e.target.value })} className="mt-1 rounded-sm" />
              </div>
              <div>
                <Label>{t('property', 'Property')}</Label>
                <Select value={form.property_title} onValueChange={(v) => setForm({ ...form, property_title: v })}>
                  <SelectTrigger className="mt-1 rounded-sm bg-white"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent className="bg-white max-h-64">{properties.map((p) => <SelectItem key={p.id} value={p.title}>{p.title}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>{t('contact', 'Contact')}</Label>
                <Select value={form.contact_name} onValueChange={(v) => setForm({ ...form, contact_name: v })}>
                  <SelectTrigger className="mt-1 rounded-sm bg-white"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent className="bg-white max-h-64">{contacts.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>{t('value', 'Value (€)')}</Label><Input type="number" value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} className="mt-1 rounded-sm" /></div>
              <div><Label>{t('commission2', 'Commission (€)')}</Label><Input type="number" value={form.commission} onChange={(e) => setForm({ ...form, commission: e.target.value })} className="mt-1 rounded-sm" /></div>
            </div>
            <div><Label>{t('nextAction', 'Next Action')}</Label><Input value={form.next_action} onChange={(e) => setForm({ ...form, next_action: e.target.value })} className="mt-1 rounded-sm" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setShowForm(false)}>{t('cancel', 'Cancel')}</Button>
            <Button data-testid="opp-save" disabled={!form.title} onClick={submit} className="rounded-sm bg-[#0B2545] hover:bg-[#1a365d] text-white">{t('create', 'Create')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
